export const policyTypes = {
  // monolines
  Home: 'H',
  Auto: 'A',
  Renters: 'R',
  Condo: 'C',
  Farm: 'F',
  FarmHome: 'Z',
  FarmRental: 'Y',
  // bundles
  ARBundle: 'AR',
  HABundle: 'HA',
  CABundle: 'CA',
  FABundle: 'FA'
};

/**
 * The set of policy type codes that use the farm infrastructure (locations, risks, liability, etc.).
 * F = full farm, Z = home (FarmHome), Y = rental (FarmRental).
 *
 * Farm policies persist their specific type in `policyType` (F/Z/Y) — the home-vs-rental
 * distinction is no longer flattened to 'F'. Any code that branches on "is this a farm policy"
 * must therefore accept all three codes; use `isFarmBasedPolicyType` rather than `=== 'F'`.
 */
export const FARM_BASED_POLICY_TYPES = new Set<string>([
  policyTypes.Farm,
  policyTypes.FarmHome,
  policyTypes.FarmRental
]);

/**
 * Returns true if the given policy type code uses the farm infrastructure.
 * Works for F (full farm), Z (home), and Y (rental).
 */
export function isFarmBasedPolicyType(type: string | null | undefined): boolean {
  return type != null && FARM_BASED_POLICY_TYPES.has(type);
}

/**
 * Returns true if the given policy / offer-option type code includes an auto line: A on its own or
 * any bundle that contains it (AR, HA, CA, FA).
 *
 * Auto-only logic — MVR / DriverRisk / A+ Auto driver-data pulls, driver violations, UDRs — must key
 * off this, never off the presence of a `drivers` node: on an offer `drivers` is simply the people
 * node, and migrated farm policy segments can carry a stale `drivers` array. #35845
 */
export function policyTypeHasAuto(type: string | null | undefined): boolean {
  return type != null && type.includes(policyTypes.Auto);
}

export const policyTypeNames: Record<string, string> = {
  H: 'Home',
  A: 'Auto',
  R: 'Renters',
  C: 'Condo',
  AR: 'Auto + Renters',
  HA: 'Home + Auto',
  CA: 'Condo + Auto',
  F: 'Farm',
  Z: 'Home',
  Y: 'Rental'
};
export const offerFarm = true;

/**
 * Interface for the global quote object containing effective dates
 */
export interface EffectiveDatesGlobal {
  homeEffectiveDate?: string;
  autoEffectiveDate?: string;
  rentersEffectiveDate?: string;
  condoEffectiveDate?: string;
  farmEffectiveDate?: string;
}

/**
 * Gets the effective date for a given option type from the global quote object.
 * For bundles, returns the latest effective date among the included policy types.
 * @param optionType - The option type (e.g., 'H', 'A', 'HA', 'AR', 'CA', 'FA')
 * @param global - The offer.quote.global object containing effective dates
 * @returns The effective date string, or undefined if not found
 */
export function getEffectiveDateForOptionType(optionType: string, global: EffectiveDatesGlobal): string | undefined {
  const effectiveDateMap: Record<string, string | undefined> = {
    [policyTypes.Home]: global.homeEffectiveDate,
    [policyTypes.Auto]: global.autoEffectiveDate,
    [policyTypes.Renters]: global.rentersEffectiveDate,
    [policyTypes.Condo]: global.condoEffectiveDate,
    [policyTypes.Farm]: global.farmEffectiveDate,
    [policyTypes.FarmHome]: global.farmEffectiveDate,
    [policyTypes.FarmRental]: global.farmEffectiveDate
  };

  // For monolines, return the corresponding effective date
  if (effectiveDateMap[optionType]) {
    return effectiveDateMap[optionType];
  }

  // For bundles, collect all applicable effective dates and return the latest
  const applicableDates: string[] = [];
  if (optionType.includes(policyTypes.Home) && global.homeEffectiveDate) {
    applicableDates.push(global.homeEffectiveDate);
  }
  if (optionType.includes(policyTypes.Auto) && global.autoEffectiveDate) {
    applicableDates.push(global.autoEffectiveDate);
  }
  if (optionType.includes(policyTypes.Renters) && global.rentersEffectiveDate) {
    applicableDates.push(global.rentersEffectiveDate);
  }
  if (optionType.includes(policyTypes.Condo) && global.condoEffectiveDate) {
    applicableDates.push(global.condoEffectiveDate);
  }
  if (optionType.includes(policyTypes.Farm) && global.farmEffectiveDate) {
    applicableDates.push(global.farmEffectiveDate);
  }

  if (applicableDates.length === 0) {
    return undefined;
  }

  // Return the latest date
  return applicableDates.reduce((latest, date) => {
    return new Date(date) > new Date(latest) ? date : latest;
  });
}
